import React, { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonSelect from "components/ArgonSelect";
import DataTable from "examples/Tables/DataTable";
import BusinessUserModal from "./components/BusinessUserModal";
import ActionModal from "components/Modals/ActionModal";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Card, Typography } from "@mui/material";
import { getBusinessUsers, deleteBusinessUser } from "services/usersServices";
import { CATEGORIES_FILTER, SUBSCRIPTION_FILTER } from "./consts";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import SearchBar from "../../Components/SearchBar";
import { useDebouncedSearch } from "../../../../hooks/useDebouncedSearch";
import { toast } from "react-toastify";
import AvatarWithLoader from "../../Components/Avatar/AvatarWithLoader";
import "./BusinessUser.css";

function BusinessUsers() {
  const [openModal, setOpenModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [menu, setMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    user: null,
  });
  const [category, setCategory] = useState(CATEGORIES_FILTER[0]);
  const [subscription, setSubscription] = useState(SUBSCRIPTION_FILTER[0]);
  const [users, setUsers] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);

  const handleOpenModal = (user = null) => {
    setEditUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setEditUser(null);
    setOpenModal(false);
  };

  const handleMenuClick = (event, user) => {
    event.preventDefault();
    const menuWidth = 100;
    setMenu({
      visible: true,
      x: event.clientX - menuWidth,
      y: event.clientY,
      user,
    });
  };

  const handleMenuClose = () => {
    setMenu({ ...menu, visible: false, user: null });
  };

  const handleDelete = (id) => {
    setDeleteUserId(id);
    setDeleteModal(true);
    handleMenuClose();
  };

  const confirmDelete = () => {
    deleteBusinessUser(deleteUserId).then((data) => {
      setRefresh(!refresh);
      setDeleteModal(false);
      setDeleteUserId(null);
    });
  };

  const cancelDelete = () => {
    setDeleteModal(false);
    setDeleteUserId(null);
  };

  // Fetch users when filters or debounced search term changes
  useEffect(() => {
    setLoading(true);
    getBusinessUsers(
      category.value !== "All" ? category.value : null,
      subscription.value !== "All" ? subscription.value : null,
      debouncedSearchTerm || null
    )
      .then((data) => {
        const transformedUsers = data.data.map((user) => ({
          _id: user._id,
          name: user.name,
          email: user.email,
          businessName: user.businessName,
          subscription: user.businessSubscriptionType,
          category: user.category,
          phone: user.phone,
          address: user.address,
          notes: user.notes,
          avatar: user.avatar,
          isDeleted: user.isDeleted,
          lastActivity: user.lastActivity,
        }));
        setUsers(transformedUsers);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to fetch business users");
        setLoading(false);
      });
  }, [category, subscription, refresh, debouncedSearchTerm]);

  const formatLastActivity = (lastActivity) => {
    if (!lastActivity) {
      return "NA";
    }
    const date = new Date(lastActivity);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      Header: "Business User",
      accessor: "user",
      maxWidth: 220,
      Cell: ({ row }) => (
        <ArgonBox display="flex" alignItems="center">
          <AvatarWithLoader src={row.original.avatar} alt={row.original.name} />
          <ArgonBox display="flex" flexDirection="column">
            <Typography
              fontWeight="bold"
              variant="button"
              color="#7B809A"
              sx={{ textTransform: "none" }}
            >
              {row.original.name}
            </Typography>
            <Typography
              variant="caption"
              color="#202020"
              fontWeight="medium"
              sx={{ fontSize: 12 }}
            >
              {row.original.email}
            </Typography>
          </ArgonBox>
        </ArgonBox>
      ),
    },
    {
      Header: "Business Name",
      accessor: "businessName",
      maxWidth: 140,
      Cell: ({ value }) => (
        <Tooltip title={value}>
          <span
            style={{
              display: "inline-block",
              maxWidth: 120,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "bold",
            }}
          >
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      Header: "Subscription",
      accessor: "subscription",
      maxWidth: 100,
      Cell: ({ value }) => (
        <span style={{ fontWeight: "bold" }}>
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      ),
    },
    {
      Header: "Category",
      accessor: "category",
      maxWidth: 100,
      Cell: ({ value }) => <span style={{ fontWeight: "bold" }}>{value}</span>,
    },
    {
      Header: "Phone",
      accessor: "phone",
      maxWidth: 120,
      Cell: ({ value }) => <span style={{ fontWeight: "bold" }}>{value}</span>,
    },
    {
      Header: "Address",
      accessor: "address",
      maxWidth: 120,
      Cell: ({ value }) => (
        <Tooltip title={value}>
          <span
            style={{
              display: "inline-block",
              maxWidth: 100,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "bold",
            }}
          >
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      Header: "Notes",
      accessor: "notes",
      maxWidth: 160,
      Cell: ({ value }) => (
        <Tooltip title={value}>
          <span
            style={{
              display: "inline-block",
              maxWidth: 140,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "bold",
            }}
          >
            <span
              style={{
                display: "inline-block",
                maxWidth: 80,
                wordBreak: "break-word",
                whiteSpace: "normal",
                fontSize: 12,
              }}
            >
              {value.length > 10 ? value.slice(0, 10) + "..." : value}
            </span>
          </span>
        </Tooltip>
      ),
    },
    {
      Header: "Last Activity",
      accessor: "lastActivity",
      maxWidth: 180,
      Cell: ({ value }) => (
        <span style={{ fontWeight: "bold" }}>
          {formatLastActivity(value)}
        </span>
      ),
    },
    {
      Header: "Action",
      accessor: "action",
      maxWidth: 60,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => handleMenuClick(e, row.original)}
          size="small"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      {/* Search Bar rendered via portal */}
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search business users"
      />

      <ArgonBox display="flex" justifyContent="flex-end" py={3}>
        <ArgonButton
          variant="outlined"
          color="#ffffff"
          onClick={() => handleOpenModal()}
        >
          Add New User
        </ArgonButton>
      </ArgonBox>

      <ArgonBox>
        <Card sx={{ overflow: "visible", padding: 3 }}>
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            p={3}
          >
            <ArgonBox
              lineHeight={1}
              display="flex"
              justifyContent="space-between"
              width="100%"
              alignItems="center"
            >
              <ArgonTypography variant="h5" fontWeight="medium">
                Business Users Management
              </ArgonTypography>
              <ArgonBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
              >
                <ArgonSelect
                  value={category}
                  options={CATEGORIES_FILTER}
                  onChange={setCategory}
                  hasShadow
                  width="100px"
                />
                <ArgonSelect
                  value={subscription}
                  options={SUBSCRIPTION_FILTER}
                  onChange={setSubscription}
                  hasShadow
                  width="120px"
                />
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>

          {loading ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </ArgonBox>
          ) : users.length > 0 ? (
            <ArgonBox
              sx={{
                "& .MuiTableRow-root": {
                  height: "48px", // Reduced from default ~64px
                },
                "& .MuiTableCell-root": {
                  padding: "8px 16px", // Reduced padding
                  fontSize: "14px", // Slightly smaller font
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  padding: "12px 16px", // Keep header padding slightly larger
                  fontSize: "13px",
                  fontWeight: "600",
                },
              }}
            >
              <DataTable
                table={{ columns, rows: users }}
                entriesPerPage={false}
                canSearch={false}
                showTotalEntries={false}
                pagination={false}
                customWidth={true}
                cellWidth={true}
              />
            </ArgonBox>
          ) : (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <ArgonTypography variant="h6">No users found</ArgonTypography>
            </ArgonBox>
          )}

          {menu.visible && (
            <div
              className="business-user:context-menu"
              style={{
                top: menu.y,
                left: menu.x,
              }}
              onMouseLeave={handleMenuClose}
            >
              <div
                className="business-user:menu-item"
                onClick={() => {
                  handleOpenModal(menu.user);
                  handleMenuClose();
                }}
              >
                Edit User
              </div>
              <div
                className={`business-user:menu-item ${menu.user.isDeleted
                  ? "business-user:is-deleted"
                  : "business-user:delete"
                  }`}
                onClick={() => {
                  if (menu.user.isDeleted) {
                    return;
                  }
                  handleDelete(menu.user._id);
                }}
              >
                Delete User
              </div>
            </div>
          )}

          {openModal && (
            <BusinessUserModal
              open={openModal}
              handleClose={handleCloseModal}
              user={editUser}
              onUpdate={() => setRefresh(!refresh)}
            />
          )}
          {deleteModal && (
            <ActionModal
              open={deleteModal}
              handleClose={cancelDelete}
              title="Delete Business User"
              detail="Are you sure you want to delete this business user? This action cannot be undone."
              handleYes={confirmDelete}
              handleNo={cancelDelete}
              color="#e53e3e"
            />
          )}
        </Card>
      </ArgonBox>
    </>
  );
}

export default BusinessUsers;
